import { computed, getCurrentInstance } from "vue";
import { useStore } from "vuex";
import { isDefined, isEmpty } from "@utils/var.check";
import DeviceUtils from "@utils/device.utils";
import DOMUtils from "../../../utils/dom.utils";
import { DRAWER_NAME, PAGE_DRAWER_TRANSITION_DURATION_MS } from "@modules/app/service/app.constant";
import PageDrawerEventBus from "@plugins/page-drawer/page.drawer.event.bus";
import { appLocalStorage } from "@utils/app.storage";

interface UsePageDrawerOptions {
  drawerName: string;
}

export function usePageDrawer({ drawerName }: UsePageDrawerOptions) {
  const store = useStore();
  const proxy = getCurrentInstance()?.proxy;
  if (!proxy) throw new Error("Proxy is not available");

  const pageDrawer = getCurrentInstance()?.appContext.config.globalProperties.$pageDrawer;
  if (!pageDrawer) {
    throw new Error("pageDrawer is not provided");
  }

  const showPageDrawer = computed(() => store.getters["app/showPageDrawer"]);

  const getPageDrawerExpanded = (drawerName: string) =>
    store.getters["app/getPageDrawerExpanded"](drawerName);

  const getPageDrawerContentType = (drawerName: string) =>
    store.getters["app/getPageDrawerContentType"](drawerName);

  const isPageDrawerStateDefined = computed(() => isDefined(getPageDrawerExpanded(drawerName)));

  const isPageDrawerExpanded = computed(() => getPageDrawerExpanded(drawerName));

  const drawerClasses = computed(() => {
    if (!drawerName || !isPageDrawerStateDefined.value) return;
    if (!isPageDrawerExpanded.value) {
      updatePageTempleteWidth();
    }
    return {
      "drawer-collapsed": !isPageDrawerExpanded.value,
      "drawer-expanded": isPageDrawerExpanded.value,
    };
  });

  const drawerToggleMenuLabel = computed(() =>
    drawerClasses.value && drawerClasses.value["drawer-expanded"]
      ? "hide_drawer_label"
      : "expand_drawer_label",
  );

  const drawerToggleMenuIcon = computed(() =>
    drawerClasses.value && drawerClasses.value["drawer-expanded"] ? "Collapse24" : "Expand24",
  );

  const drawerToggleIcon = computed(() =>
    drawerClasses.value && drawerClasses.value["drawer-expanded"]
      ? "GearCollapse32"
      : "GearExpand32",
  );

  const notesToggleIcon = computed(() =>
    drawerClasses.value && drawerClasses.value["drawer-expanded"]
      ? "NoteCollapse32"
      : "NoteExpand32",
  );

  function handleToggle(data) {
    if (!drawerName) return;

    const drawerContentType = data?.drawerContentType ?? undefined;
    if (isPageDrawerExpanded.value) {
      if (!isEmpty(drawerContentType)) {
        if (drawerContentType === getPageDrawerContentType(drawerName)) {
          handleCollapseDrawer({ isUserAction: data.isUserAction });
        } else {
          updatePageDrawerContentType(drawerContentType);
        }
      } else {
        handleCollapseDrawer({});
      }
    } else {
      handleExpandDrawer({ drawerContentType: drawerContentType });
    }
  }

  function handleExpandDrawer(data) {
    store.dispatch("app/addPageDrawerExpanded", { drawerName: drawerName, isExpanded: true });
    updatePageDrawerContentType(data?.drawerContentType ?? undefined);
    pageDrawer?.expand(drawerName);
    PageDrawerEventBus.$emit("pageDrawerToggleState", isPageDrawerExpanded.value);
  }

  function handleCollapseDrawer(data) {
    store.dispatch("app/addPageDrawerExpanded", {
      drawerName: drawerName,
      isExpanded: false,
    });

    if (data?.isUserAction) {
      updatePageDrawerContentType("");
    }

    pageDrawer?.collapse(drawerName);
    PageDrawerEventBus.$emit("pageDrawerToggleState", isPageDrawerExpanded.value);

    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, PAGE_DRAWER_TRANSITION_DURATION_MS);
    });
  }

  function updatePageDrawerContentType(drawerContentType) {
    if (isDefined(drawerContentType)) {
      store.dispatch("app/addPageDrawerContentType", {
        drawerName: drawerName,
        drawerContentType: drawerContentType,
      });

      const key = `page-drawer-${drawerName}-content-type`;
      appLocalStorage.setItem(key, drawerContentType);
    }
  }

  function setOnloadDrawerState() {
    if (!drawerName) return;

    if (isPageDrawerStateDefined.value) {
      if (isPageDrawerExpanded.value) {
        handleExpandDrawer();
      } else {
        handleCollapseDrawer();
      }
      return;
    }

    if (drawerName === DRAWER_NAME.ATTEMPT || drawerName === DRAWER_NAME.SETTINGS) {
      if (showPageDrawer.value) {
        handleExpandDrawer();
      } else {
        handleCollapseDrawer();
      }
      return;
    }

    if (DeviceUtils.isMobile() || DeviceUtils.isTablet()) {
      handleCollapseDrawer();
    }
  }

  function removeDrawerState() {
    store.dispatch("app/removePageDrawerExpanded", { drawerName: drawerName });
    pageDrawer?.unregister(drawerName);
  }

  function updatePageTempleteWidth() {
    if (!proxy?.$el) return;
    const pageElement = proxy?.$el as HTMLElement;
    if (!DOMUtils.hasClass(pageElement, "page-template")) {
      return;
    }
    const width = pageElement.clientWidth;
    DOMUtils.setCSSVariable("--pagewidth", `${width}px`);
  }

  return {
    showPageDrawer,
    isPageDrawerExpanded,
    drawerClasses,
    drawerToggleMenuLabel,
    drawerToggleMenuIcon,
    drawerToggleIcon,
    notesToggleIcon,
    handleToggle,
    handleExpandDrawer,
    handleCollapseDrawer,
    setOnloadDrawerState,
    removeDrawerState,
    getPageDrawerExpanded,
    getPageDrawerContentType,
    updatePageDrawerContentType,
    updatePageTempleteWidth,
  };
}
